import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Header } from "@/components/Header";
import {
  clearNewSuggestedDataFlag,
  getConsultantProfileCompany,
  IConsultantProfile,
  IConsultantProfileLanguage,
  putConsultantProfile,
} from "@/data/consultant-profiles";
import { IConsultantProfileRole } from "@/data/roles";
import { Flex, IDialogModal, inDialogModal } from "@/eworkitComponents";
import {
  IProfileSuggestedDataFormValues,
  ProfileSuggestedDataFormik,
} from "@/views/components/ConsultantProfile/SuggestedData";
import { StyledModal } from "@/views/components/Modal";

export interface ISuggestedDataDialogProps {
  profile: IConsultantProfile;
  onProfileUpdate: (profile: IConsultantProfile) => void;
}

const SuggestedDataDialog: FC<ISuggestedDataDialogProps & IDialogModal> = ({ profile, onClose, onProfileUpdate }) => {
  const { t } = useTranslation("session");

  const handleSubmit = useCallback(
    async (values: IProfileSuggestedDataFormValues) => {
      try {
        await putConsultantProfile(
          { consultantProfileId: profile.id, companyId: getConsultantProfileCompany(profile)?.id },
          { ...profile, ...values },
        );
        await clearNewSuggestedDataFlag(
          { consultantProfileId: profile.id, companyId: getConsultantProfileCompany(profile)?.id },
          {},
        );

        onProfileUpdate({ ...profile, ...values, newSuggestedData: false });
        onClose();
      } catch (error) {
        // TODO: handle error if specified
      }
    },
    [profile, onProfileUpdate, onClose],
  );

  return (
    <StyledModal>
      <Flex direction="column" margin={["xl"]} innerMargin="xl">
        <Header color="black" size="md" weight="bold">
          {t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.DIALOG.SUGGESTED_CHANGES")}
        </Header>

        <ProfileSuggestedDataFormik
          onCancel={onClose}
          onSubmit={handleSubmit}
          profile={profile}
          buttonCancelText={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.DIALOG.BUTTON_CANCEL")}
          buttonSubmitText={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.DIALOG.BUTTON_SUBMIT")}
          initialValues={{
            ...profile,
            roles: profile.roles || [{} as IConsultantProfileRole],
            skills: profile.skills || [],
            languages: profile.languages || [{} as IConsultantProfileLanguage],
            location: profile.location || null,
          }}
        />
      </Flex>
    </StyledModal>
  );
};

export const SuggestedDataDialogModal = inDialogModal(SuggestedDataDialog);
